'use client';
import { queryFeeds } from '@/apis/land/land.api';
import UKLoadingIcon, { CIRCLE_COLOR_MAP, SIZE_MAP } from '@/components/UKLoadingIcon';
import ProductCard from '@/components/productCard';
import { ProductMode, ProductState, ProductType } from '@/components/productCard/index.interface';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { getExposureRecord, sendClickTrack } from '@/app/cms/tempo_track';
import { formatProductLink } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { cookieUtil } from '@/intl-jdi-common-ssr/utils/cookieUtils';
import Masonry from 'react-masonry-css';
import style from './content.module.scss';
import { observer } from 'mobx-react-lite';
import { usePageShowObserver } from '@/hooks/usePageShowObserver';
import { addCartUtil } from '../../../../utils/cartUtils';

interface ContentProps {
  skuList: any[];
  localInfo?: any;
  dataExternalKey: string;
  shouldDisplayCart: boolean;
  paramsData?: any;
  isShowProType?: boolean;
  pos?: any;
  pageCode?: any;
  moduleInstanceId?: any;
}
export const Content = observer((props: ContentProps) => {
  const {
    skuList,
    localInfo,
    dataExternalKey,
    shouldDisplayCart,
    paramsData,
    isShowProType = false, //加车时是否弹起规格弹层
    pos,
    pageCode,
    moduleInstanceId,
  } = props;

  const cuPageRef = useRef(2);
  const { t } = useTranslation();
  const lastScrollIdRef = useRef<number>(skuList[skuList.length - 1]?.extInfo?.scrollId);
  const stopGetProductRef = useRef(skuList?.[skuList.length - 1]?.extInfo?.lastSkuTag || false); //是否停止获取数据
  const [loading, setLoading] = useState(false); //是否在加载数据
  const [productList, setProductList] = useState<any[]>([...skuList]);
  const [isShowTryAgain, setIsShowTryAgain] = useState(false); //是否展示tryAgain
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { cartNumStore } = useStores(); //购物车浮层数据全局管理

  usePageShowObserver({
    callback: () => {
      cartNumStore.fetchCartNum(true);
    },
  });
  //------------------------ 商品&加购 点击和曝光 埋点 -------------------------

  const exposureRecord = getExposureRecord(pageCode);
  const genJsonParam = (product: any, index: number) => {
    const json_param: any = {
      page_id: pageCode,
      moduleInstanceId: moduleInstanceId,
      pos: pos,
      index: index,
      skuid: isNaN(product?.skuId) ? '-100' : product.skuId.toString(),
    };

    // 根据 paramsData 添加额外字段
    if (paramsData?.productPoolId) {
      json_param.propoolid = paramsData.productPoolId;
    }
    if (paramsData?.activityId) {
      json_param.activityid = paramsData.activityId;
    }
    if (product.algoRdInfo) {
      json_param.propoolid = product.productPoolId;
      json_param.broker_info = product.brokerInfo ? JSON.stringify(product.brokerInfo) : '';
      json_param.algo_rd_inf = product.algoRdInfo;
    }
    if (paramsData?.type === 'EXT_CHANNEL_ACTIVITY_PRODUCT') {
      const attributionCookie = cookieUtil.getCookie('_attribution');
      const attribution = attributionCookie ? JSON.parse(attributionCookie) : { utm: {} };
      json_param.channelName = attribution.utm.utm_source ? attribution.utm.utm_source : 'organic';
      json_param.activityid = paramsData.productPoolId;
    }

    return json_param;
  };

  const genEventTrackingInfo = (product: any, index: number) => {
    const eventTrackingInfo = {} as { [key: string]: any };
    const json_param = genJsonParam(product, index);
    const expConfig = {
      useClick: false,
      stay_duration: 0.3,
      repeated: false,
      area_rate: 0.5,
    };

    if (paramsData?.type === 'EXT_CHANNEL_ACTIVITY_PRODUCT') {
      eventTrackingInfo['productClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_FeedsProductPool_click(json_param);
      eventTrackingInfo['cartClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_FeedsProductPool_addtocart_click(json_param);
      eventTrackingInfo['productExpConfig'] = {
        event_id: exposureRecord.cms_TempoPage_FeedsProductPool_expo,
        config: expConfig,
        json_param: json_param,
      };
    } else if (paramsData && paramsData?.activityId) {
      eventTrackingInfo['productClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_BundledealPool_click(json_param);
      eventTrackingInfo['cartClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_BundledealPool_addtocart_click(json_param);
      eventTrackingInfo['productExpConfig'] = {
        event_id: exposureRecord.cms_TempoPage_BundledealPool_expo,
        config: expConfig,
        json_param: json_param,
      };
    } else if (paramsData && paramsData?.productPoolId) {
      eventTrackingInfo['productClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_FeedsProductPool_click(json_param);
      eventTrackingInfo['cartClickPoint'] = () =>
        sendClickTrack.cms_TempoPage_FeedsProductPool_addtocart_click(json_param);
      eventTrackingInfo['productExpConfig'] = {
        event_id: exposureRecord.cms_TempoPage_FeedsProductPool_expo,
        config: expConfig,
        json_param: json_param,
      };
    }

    return eventTrackingInfo;
  };
  const gotoCart = async (skuId: string) => {
    const res = await addCartUtil({
      productList: [{ skuId, num: '1' }],
    });
    if (res?.result?.code === '200') {
      // 更新购物车数量
      cartNumStore.updateCartNum(res.result.data);
    }
  };

  const getMoreProduct = async () => {
    if (stopGetProductRef.current || loading) {
      return;
    }
    setLoading(true);
    setIsShowTryAgain(false);

    // 构建请求参数
    const _params = {
      type: dataExternalKey,
      cuPage: cuPageRef.current,
      pageSize: 10,
      mapExInfo: {
        ...paramsData,
        lastScrollId: lastScrollIdRef.current,
      },
    };
    // 如果没有缓存数据，请求推荐商品
    const res = await queryFeeds(_params).catch((err) => {});
    if (res) {
      const list = res.data.map((sku: any, index) => {
        sku.url = formatProductLink(sku.skuId, sku.skuName);
        if (index === res.data.length - 1) {
          lastScrollIdRef.current = sku?.extInfo?.scrollId;
          stopGetProductRef.current = sku?.extInfo?.lastSkuTag || false;
        }
        return sku;
      });
      setProductList([...productList, ...list]);
      cuPageRef.current = res.currentPage + 1;
    }
    setLoading(false);
  };

  //app回调H5再次上屏触发pageShow方法
  const pageShow = () => {
    getCartNum();
  };

  const getCartNum = () => {
    cartNumStore.fetchCartNum(true);
  };

  const getSaleNum = (labels: any) => {
    if (!labels) {
      return '';
    }
    const { labelKeys, labelData } = labels;
    if (labelKeys.includes('15')) {
      return labelData['15'].value;
    }
    return '';
  };

  useEffect(() => {
    let JSBridge: any;

    //懒加载APP通信桥方法
    const setupEffect = async () => {
      const bridgeModule = await import('@/utils/bridge');
      JSBridge = bridgeModule.JSBridge;

      //挂载app向h5通信pageShow方法
      JSBridge.eventEmitter.on('pageShow', pageShow);
      getCartNum();
    };
    setupEffect();

    //创建IntersectionObserver实例，监听loadMoreRef元素是否在视口内来实现加载更多功能
    const options = {
      root: null, // 使用视口作为容器
      rootMargin: '0px',
      threshold: 0.5, // 当目标元素完全可见时触发
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          getMoreProduct(); // 触发加载更多事件
        }
      });
    }, options);
    const loadMoreElement = loadMoreRef.current;
    if (loadMoreElement) {
      observer.observe(loadMoreElement);
    }

    return () => {
      //清除pageShow方法
      if (JSBridge) JSBridge.eventEmitter.off('pageShow');
      if (observer) observer.disconnect();
    };
  }, [productList]);

  return (
    <div className={style['uk-h5-land']}>
      <Masonry className={style.productList} columnClassName={style.productCol} breakpointCols={2}>
        {productList.length
          ? productList.map((item, index) => {
              const productData = {
                type: ProductType.TYPE_4,
                mode: ProductMode.VERTICAL,
                // 默认都是有货的
                productState: ProductState.IN_STOCK,
                width: '100%',
                containerStyle: { backgroundColor: '#fff' },
                skuName: item.skuName,
                jumpUrl: item?.jumpUrl ? item?.jumpUrl : null,
                skuImgKeyParams: {
                  imgUrlKey: item?.imageUrl,
                  width: 200,
                  height: 200,
                },
                skuImgStyle: { width: '100%', aspectRatio: '1/1', borderRadius: '0px' },
                skuId: item.skuId,
                realPrice: item?.realPrice,
                originalPrice: item?.originalPriceV2,
                originalPriceTips: item?.originalPriceDesc,
                currency: localInfo?.currencyCode,
                languageCode: localInfo?.languageCode,
                shouldShowScore: true,
                score: item.extInfo?.commentScore > 3 ? item.extInfo?.commentScore : '',
                shouldShowCart: true,
                saleNum: getSaleNum(item.extInfo?.labels),
                onAddCartSuccess: getCartNum,
                onClickCart: isShowProType ? null : () => gotoCart(item.skuId),
                promotions: Object.values(item?.extInfo?.labels?.labelData || {}),
                trackingPointParams: genEventTrackingInfo(item, index),
                cartLoadingConfig: {
                  type: CIRCLE_COLOR_MAP.WHITE,
                  size: SIZE_MAP.SIZE_24,
                },
                wishLoadingConfig: {
                  type: CIRCLE_COLOR_MAP.BLACK,
                  size: SIZE_MAP.SIZE_24,
                },
                // 暂时不支持心愿单
                isShowWish: false,
              };
              return (
                <div className={style.product} key={item.skuId}>
                  {/* @ts-ignore */}
                  <ProductCard productData={productData} />
                </div>
              );
            })
          : null}
      </Masonry>
      <div ref={loadMoreRef} className={style.loadMoreIndicator}>
        {loading && (
          <div className={style.loading}>
            <UKLoadingIcon type={CIRCLE_COLOR_MAP.BLACK} size={SIZE_MAP.SIZE_24} />
          </div>
        )}
      </div>
      {/* <CartLayer shouldDisplayCart={shouldDisplayCart} /> */}
    </div>
  );
});
